import { shutdown } from "@intercom/messenger-js-sdk"
import { replace } from "connected-react-router"
import { push } from "redux-first-history"
import { call, delay, put, select, takeLatest } from "redux-saga/effects"

import { getUserId, setPassedRegistration } from "../../hooks/useAttributionTracking"
import ErrorsActions from "../../Redux/ErrorsRedux"
import { defaultInnerPage } from "../../Sagas/StartupSagas"
import { request } from "../../Services/Api"
import {
  getAdminPostLoginRoute,
  getAdminTwoFactorSetupRoute,
  getAdminTwoFactorVerificationRoute
} from "../../Utils/adminTwoFactor"
import { isOnboardingFinished } from "../../Utils/onboarding/isOnboardingFinished"
import { adminRoleId, superAdminRoleId } from "../../Utils/permission"
import utils, {
  createAccountStepPath,
  excludeReferences,
  onboardingStepPath
} from "../../Utils/utils"
import NavActions from "../User/actions"
import UserActions, { UserSelectors } from "../User/reducer"
import AuthActions, { AuthTypes } from "./reducer"
import * as services from "./services"
import { getOAuthRedirectUrl } from "./utils/socialAuthRedirect.util"

const isAdminUser = (userInfo) => [adminRoleId, 5, superAdminRoleId].includes(userInfo?.role?.id)

export function* signIn(api, { data }) {
  try {
    const { redirect, next_page, ...requestData } = data
    const response = yield call(api.signIn, requestData)
    const historyLocal = yield select((state) => state.router)

    if (response && response.access && response.userInfo) {
      yield put(AuthActions.signInSuccess(response.userInfo, response.access, response.refresh))

      if (response.userInfo?.["is_premium_available"])
        yield put(AuthActions.setShowModalPremiumFeatures(true))

      yield delay(500)

      const oauthRedirect = localStorage.getItem("OAuthRedirect")

      if (oauthRedirect) {
        localStorage.removeItem("OAuthRedirect")
        window.location.href = oauthRedirect
      } else if (next_page) {
        window.location.href = next_page
      } else if (redirect) {
        //oAuth redirect logic
        if (!isAdminUser(response.userInfo)) {
          let decodedNextUrl
          try {
            decodedNextUrl = decodeURIComponent(redirect)
          } catch (e) {
            console.error("Error decoding nextUrl:", e)
            decodedNextUrl = redirect // используем оригинальный URL, если декодирование не удалось
          }

          const responseOauth = yield call(request.api.post, decodedNextUrl, {
            maxRedirects: 0,
            validateStatus: (status) => status >= 200 && status < 400
          })

          if (responseOauth?.data?.redirect_uri) {
            yield put(utils.goLink(responseOauth?.data?.redirect_uri))
          } else {
            if (
              isOnboardingFinished(response.userInfo) &&
              !isAdminUser(response.userInfo) &&
              response.userInfo?.["email_added"]
            ) {
              let reference = historyLocal?.location?.state?.reference
              if (reference && !excludeReferences.includes(reference)) {
                yield put(replace(reference))
              } else {
                yield put(push(defaultInnerPage))
              }
            } else if (isAdminUser(response.userInfo)) {
              yield put(NavActions.changeTheme("theme", "light"))
              yield put(push(getAdminPostLoginRoute(response.userInfo)))
            } else if (!response.userInfo?.["email_added"]) {
              yield put(push("/add-email"))
            } else {
              if (!response.userInfo?.["is_email_verified"]) {
                yield put(push("/getting_started"))
              } else {
                if (response.userInfo?.["next_step"])
                  yield put(push(onboardingStepPath[response.userInfo?.["next_step"]]))
              }
            }
          }

          //if(!responseOauth.ok) yield put(push('/dashboard'));
          //yield put(utils.goLink(decodedNextUrl))
        }
      } else {
        if (
          isOnboardingFinished(response.userInfo) &&
          !isAdminUser(response.userInfo) &&
          response.userInfo?.["email_added"]
        ) {
          let reference = historyLocal?.location?.state?.reference
          if (reference && !excludeReferences.includes(reference)) {
            yield put(replace(reference))
          } else {
            yield put(push(defaultInnerPage))
          }
        } else if (isAdminUser(response.userInfo)) {
          yield put(NavActions.changeTheme("theme", "light"))
          yield put(push(getAdminPostLoginRoute(response.userInfo)))
        } else if (!response.userInfo?.["email_added"]) {
          yield put(push("/add-email"))
        } else {
          if (!response.userInfo?.["is_email_verified"]) {
            yield put(push("/getting_started"))
          } else {
            if (response.userInfo?.["next_step"] && !response.userInfo?.is_created_by_invitation) {
              yield put(push(onboardingStepPath[response.userInfo?.["next_step"]]))
            } else {
              yield put(push("/dashboard"))
            }
          }
        }
      }
    } else if (response && response.data) {
      if (response.status === 401) {
        const errorPayload = response?.data?.message || response?.data

        if (errorPayload?.code === "mfa_required" && errorPayload?.link) {
          const is2FAConfigured =
            typeof errorPayload?.is_2fa_configured === "string"
              ? errorPayload.is_2fa_configured.toLowerCase() === "true"
              : Boolean(errorPayload?.is_2fa_configured)

          yield put(AuthActions.authSetProp("ephemeralToken", errorPayload.link))
          yield put(AuthActions.authSetProp("is2FAConfigured", is2FAConfigured))
          yield put(AuthActions.authErrorReset())
          yield put(
            push(
              is2FAConfigured
                ? getAdminTwoFactorVerificationRoute(errorPayload.link)
                : getAdminTwoFactorSetupRoute(errorPayload.link)
            )
          )
          return
        }

        //for admin changing password logic
        const message = response?.data?.message
        if (response?.data?.code === "password_expired" && response?.data?.isAdmin) {
          yield put(AuthActions.signInFailure(response.data))
          yield delay(500)
          yield put(AuthActions.authSetProp("tempEmail", requestData?.email))
          yield put(AuthActions.authErrorReset())
          yield delay(500)
          yield put(push("/create-new-password"))
          //
        } else {
          yield put(AuthActions.signInFailure(response.data))
        }
      } else if (response.status === 500) {
        yield put(AuthActions.signInFailure("Server response error"))
      } else {
        yield put(AuthActions.signInFailure(response.data))
      }
    } else {
      yield put(AuthActions.signInFailure("Server response error"))
    }
  } catch (e) {
    if (e?.status === 200 && e?.data?.otp_required) yield put(AuthActions.signInFailure(e?.data))
    else yield put(AuthActions.signInFailure(`Unknown error: ${e}`))
  }
}

export function* verify2FA(api, { data }) {
  try {
    const stateEphemeralToken = yield select((state) => state.auth.ephemeralToken)
    const pathname =
      (yield select((state) => state.router?.location?.pathname)) || document.location.pathname
    const search =
      (yield select((state) => state.router?.location?.search)) || document.location.search
    const searchParams = new URLSearchParams(search)
    const pathToken = pathname.startsWith("/admin/2fa-verification/")
      ? pathname.replace("/admin/2fa-verification/", "")
      : ""
    const ephemeralToken = decodeURIComponent(
      data?.ephemeral_token || searchParams.get("token") || pathToken || stateEphemeralToken || ""
    )

    const requestData = {
      ...(ephemeralToken && { ephemeral_token: ephemeralToken }),
      ...(data?.otp && { otp: data.otp }),
      ...(data?.recovery_code && { recovery_code: data.recovery_code }),
      ...(typeof data?.trust_device === "boolean" && {
        trust_device: data.trust_device
      })
    }

    const response = yield call(api.verify2FA, requestData)

    if (response?.access && response?.userInfo) {
      yield put(AuthActions.signInSuccess(response.userInfo, response.access, response.refresh))

      yield delay(500)
      yield put(NavActions.changeTheme("theme", "light"))
      yield put(push(getAdminPostLoginRoute(response.userInfo)))
      return
    }

    if (response?.data) {
      if (response.status === 500) {
        yield put(AuthActions.verify2FAFailure("Server response error"))
      } else {
        yield put(AuthActions.verify2FAFailure(response.data))
      }
    } else {
      yield put(AuthActions.verify2FAFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.verify2FAFailure(e?.data || e || "Server response error"))
  }
}

export function* generate2FASetup(api, { data }) {
  try {
    const response = yield call(api.generate2FASetup, data)

    if (response?.setup_token) {
      yield put(AuthActions.generate2FASetupSuccess(response))
      return
    }

    if (response?.data) {
      if (response.status === 401) {
        yield put(AuthActions.generate2FASetupFailure(response.data))
        yield put(ErrorsActions.errorSave(response?.data?.message || response.data))
        yield put(replace("/login"))
        return
      }

      if (response.status === 500) {
        yield put(AuthActions.generate2FASetupFailure("Server response error"))
      } else {
        yield put(AuthActions.generate2FASetupFailure(response.data))
      }
    } else {
      yield put(AuthActions.generate2FASetupFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.generate2FASetupFailure(e?.data || e || "Server response error"))
  }
}

export function* confirm2FASetup(api, { data }) {
  try {
    const response = yield call(api.confirm2FASetup, data)

    if (response?.access && response?.refresh) {
      const userInfo = yield call(api.getCurrentUser)

      if (userInfo?.id || userInfo?.role) {
        yield put(
          AuthActions.confirm2FASetupSuccess(
            userInfo,
            response.access,
            response.refresh,
            response.recoveryCodes,
            response.commitToken
          )
        )
        yield put(NavActions.changeTheme("theme", "light"))
        return
      }

      yield put(AuthActions.confirm2FASetupFailure("Server response error"))
      return
    }

    if (response?.commitToken || response?.recoveryCodes?.length) {
      yield put(
        AuthActions.confirm2FASetupSuccess(
          null,
          null,
          null,
          response.recoveryCodes,
          response.commitToken
        )
      )
      return
    }

    if (response?.data) {
      if (response.status === 500) {
        yield put(AuthActions.confirm2FASetupFailure("Server response error"))
      } else {
        yield put(AuthActions.confirm2FASetupFailure(response.data))
      }
    } else {
      yield put(AuthActions.confirm2FASetupFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.confirm2FASetupFailure(e?.data || e || "Server response error"))
  }
}

export function* generateAdminSecurity2FA(api) {
  try {
    const accessToken = yield select((state) => state.auth.accessToken)
    const refreshToken = yield select((state) => state.auth.refreshToken)

    if (accessToken) {
      api.updateAccessToken(accessToken, refreshToken)
    }

    const response = yield call(api.generateAdminSecurity2FA)

    if (response?.setup_token) {
      yield put(AuthActions.generateAdminSecurity2FASuccess(response))
      return
    }

    if (response?.data) {
      if (response.status === 500) {
        yield put(AuthActions.generateAdminSecurity2FAFailure("Server response error"))
      } else {
        yield put(AuthActions.generateAdminSecurity2FAFailure(response.data))
      }
    } else {
      yield put(AuthActions.generateAdminSecurity2FAFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.generateAdminSecurity2FAFailure(e?.data || e || "Server response error"))
  }
}

export function* confirmAdminSecurity2FA(api, { data }) {
  try {
    const accessToken = yield select((state) => state.auth.accessToken)
    const refreshToken = yield select((state) => state.auth.refreshToken)

    if (accessToken) {
      api.updateAccessToken(accessToken, refreshToken)
    }

    const response = yield call(api.confirmAdminSecurity2FA, data)

    if (response?.commitToken || response?.recoveryCodes) {
      yield put(
        AuthActions.confirmAdminSecurity2FASuccess(response.recoveryCodes, response.commitToken)
      )
      return
    }

    if (response?.data) {
      if (response.status === 500) {
        yield put(AuthActions.confirmAdminSecurity2FAFailure("Server response error"))
      } else {
        yield put(AuthActions.confirmAdminSecurity2FAFailure(response.data))
      }
    } else {
      yield put(AuthActions.confirmAdminSecurity2FAFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.confirmAdminSecurity2FAFailure(e?.data || e || "Server response error"))
  }
}

export function* commitAdminSecurity2FA(api, { data }) {
  try {
    const accessToken = yield select((state) => state.auth.accessToken)
    const refreshToken = yield select((state) => state.auth.refreshToken)

    if (accessToken) {
      api.updateAccessToken(accessToken, refreshToken)
    }

    const response = yield call(api.commitAdminSecurity2FA, data)

    if (response?.success && response?.access && response?.refresh) {
      const userInfo = yield call(api.getCurrentUser)

      if (userInfo?.id || userInfo?.role) {
        yield put(AuthActions.commitAdminSecurity2FASuccess())
        yield put(AuthActions.signInSuccess(userInfo, response.access, response.refresh))
        return
      }

      yield put(AuthActions.commitAdminSecurity2FAFailure("Server response error"))
      return
    }

    if (response?.data) {
      if (response.status === 500) {
        yield put(AuthActions.commitAdminSecurity2FAFailure("Server response error"))
      } else {
        yield put(AuthActions.commitAdminSecurity2FAFailure(response.data))
      }
    } else {
      yield put(AuthActions.commitAdminSecurity2FAFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.commitAdminSecurity2FAFailure(e?.data || e || "Server response error"))
  }
}

export function* acceptAdminInvite(api, { data }) {
  try {
    const response = yield call(api.acceptAdminInvite, data)

    if (response?.ephemeralToken) {
      yield put(AuthActions.acceptAdminInviteSuccess(response.ephemeralToken))
      yield put(push(getAdminTwoFactorSetupRoute(response.ephemeralToken)))
      return
    }

    if (response?.data) {
      if (response.status === 500) {
        yield put(AuthActions.acceptAdminInviteFailure("Server response error"))
      } else {
        yield put(AuthActions.acceptAdminInviteFailure(response.data))
      }
    } else {
      yield put(AuthActions.acceptAdminInviteFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.acceptAdminInviteFailure(e?.data || e || "Server response error"))
  }
}

export function* createAccount(api, { data, query, isReferralOutsider }) {
  try {
    const frontend_user_id = getUserId()
    const response = yield call(api.createAccount, {
      data: {
        ...data,
        frontend_user_id
      },
      query,
      isReferralOutsider
    })
    if (response && response.data.access && response.data.user) {
      window.dataLayer = window?.dataLayer || []
      window.dataLayer.push({
        event: "user_registered",
        email: response.data.user?.email
      })

      yield put(
        AuthActions.createAccountSuccess(
          response.data.user,
          response.data.access,
          response.data.refresh
        )
      )
      api.updateAccessToken(response.data.access, response.data.refresh)

      //make request for check if need to push event in window.datalayer
      yield delay(500)
      yield put(UserActions.trackEventRequest({ event_type: "sign_up" }))
      setPassedRegistration()

      const oauthRedirect = localStorage.getItem("OAuthRedirect")

      if (data?.invite) {
        yield put(push(`/dashboard?invite=${data?.invite}`))
      } else if (oauthRedirect) {
        localStorage.removeItem("OAuthRedirect")
        window.location.href = oauthRedirect
      } else {
        if (process.env.REACT_APP_TYPE === "partner") {
          yield put(push("/check-mailbox"))
        } else {
          yield put(push("/getting_started"))
        }
      }
    } else if (response && response.data) {
      if (response.status === 401) {
        yield put(AuthActions.createAccountFailure(response.data))
      } else if (response.status === 500) {
        yield put(AuthActions.createAccountFailure("Server response error"))
      } else {
        yield put(AuthActions.createAccountFailure(response.data))
      }
    } else {
      yield put(AuthActions.createAccountFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.createAccountFailure(`Unknown createAccountFailure: ${e}`))
  }
}

export function* resendVerificationLink(api, { data }) {
  try {
    const response = yield call(api.resendVerificationLink, data)
    if (response && response.ok) {
      yield put(AuthActions.authErrorReset())
    } else if (response && response.data) {
      if (response.status === 401) {
        yield put(AuthActions.signInFailure(null))
      } else if (response.status === 500) {
        yield put(AuthActions.signInFailure("Server response error"))
      } else {
        yield put(AuthActions.signInFailure(response.data))
      }
    } else {
      yield put(AuthActions.signInFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.signInFailure(`Unknown error: ${e}`))
  }
}

export function* changeEmail(api, { data }) {
  try {
    const response = yield call(api.changeEmail, data)
    if (response && response.ok) {
      yield put(AuthActions.authErrorReset())
      yield put(push("/getting_started", data))
    } else if (response && response.data) {
      if (response.status === 401) {
        yield put(AuthActions.signInFailure(null))
      } else if (response.status === 500) {
        yield put(AuthActions.signInFailure("Server response error"))
      } else {
        yield put(AuthActions.signInFailure(response.data))
      }
    } else {
      yield put(AuthActions.signInFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.signInFailure(`Unknown error: ${e}`))
  }
}

export function* sendConfirmEmail(api, payload) {
  try {
    const email = payload.data.email
    const response = yield call(api.sendConfirmEmail, email)

    if (response?.ok) {
      yield put(AuthActions.sendConfirmEmailSuccess(response?.data?.time_to_next_try ?? null))
    } else if (response?.data) {
      if (response.status === 401) {
        yield put(AuthActions.sendConfirmEmailFailure(null))
      } else if (response.status === 500) {
        yield put(AuthActions.sendConfirmEmailFailure("Server response error"))
      } else {
        yield put(AuthActions.sendConfirmEmailFailure(response.data))
      }
    } else {
      yield put(AuthActions.sendConfirmEmailFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.sendConfirmEmailFailure(e))
  }
}

export function* confirmEmail(api, { hash, redirect }) {
  try {
    // TODO:Break the onboradin
    // yield put(AuthActions.clearState());
    const response = yield call(api.confirmEmail, hash)
    if (response && response.access && response.userInfo) {
      yield put(AuthActions.signInSuccess(response.userInfo, response.access, response.refresh))
      yield put(AuthActions.updateProfile({ next_step: 3 }))
      yield put(
        push(
          process.env.REACT_APP_TYPE === "partner"
            ? defaultInnerPage
            : "/getting_started?emailVerified=true"
        )
      )
      // yield put(push('/onboarding/questions'));
    } else {
      if (response?.data) {
        if (response.status === 401) {
          yield put(AuthActions.signInFailure(null))
        } else if (response.status === 500) {
          yield put(AuthActions.signInFailure("Server response error"))
        } else {
          yield put(AuthActions.signInFailure(response.data))
        }
      } else {
        yield put(AuthActions.signInFailure("Server response error"))
      }
      if (redirect) {
        yield put(push(redirect))
      } else {
        yield put(push("/getting_started"))
      }
    }
  } catch (e) {
    yield put(AuthActions.signInFailure(`Unknown error: ${e}`))
  }
}

export function* confirmEmailInit(api, { hash, redirect }) {
  try {
    yield put(AuthActions.clearState())
    const response = yield call(api.confirmEmailInit, hash)
    if (response && response?.codeValid) {
      yield put(AuthActions.checkEmailInitSuccess(response?.codeValid))
    } else {
      if (response?.data) {
        if (response.status === 401) {
          yield put(AuthActions.signInFailure(null))
          yield put(AuthActions.logout())
        } else if (response.status === 500) {
          yield put(AuthActions.signInFailure("Server response error"))
        } else {
          yield put(AuthActions.signInFailure(response.data))
        }
      } else {
        yield put(AuthActions.signInFailure("Server response error"))
      }
      if (redirect) {
        yield put(push(redirect))
      } else {
        yield put(push("/login"))
      }
    }
  } catch (e) {
    yield put(AuthActions.signInFailure(`Unknown error: ${e}`))
  }
}

export function* checkPassword(api, { email }) {
  try {
    const response = yield call(api.checkPassword, email)
    if (response && response.ok) {
      yield put(push("/verify-code", email))
      yield put(AuthActions.authErrorReset())
    } else if (response?.data) {
      if (response.status === 400) {
        yield put(push("/verify-code", email)) //not show error & redirect to the next step
      }
      if (response.status === 401) {
        yield put(AuthActions.signInFailure(null))
      } else if (response.status === 500) {
        yield put(AuthActions.signInFailure("Server response error"))
      } else {
        yield put(AuthActions.signInFailure(response.data))
      }
    } else {
      yield put(AuthActions.signInFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.signInFailure(`Unknown error: ${e}`))
  }
}

export function* confirmPassword(api, { code }) {
  try {
    const response = yield call(api.confirmPassword, code)
    if (response && response.access && response.userInfo) {
      yield put(AuthActions.signInSuccess(null, response.access, response.refresh))
      yield put(push("/restore-password/code"))
    } else if (response?.data) {
      if (response.status === 401) {
        yield put(AuthActions.signInFailure(null))
      } else if (response.status === 500) {
        yield put(AuthActions.signInFailure("Server response error"))
      } else {
        yield put(AuthActions.signInFailure(response.data))
      }
    } else {
      yield put(AuthActions.signInFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.signInFailure(`Unknown error: ${e}`))
  }
}

export function* createUserPassword(api, { password, isNewUserCheck, confirm_code }) {
  try {
    const response = yield call(api.createUserPassword, password, confirm_code)
    if (response && response.ok) {
      if (isNewUserCheck) {
        yield put(AuthActions.signUpSuccess(response.data))
        yield delay(500)
        yield put(push("/onboarding/plan"))
      } else {
        const successMessage = response?.data?.message?.detail || "Password set successfully!"
        yield put(AuthActions.signUpSuccess(successMessage))
        yield delay(1000)
        //yield put(AuthActions.logout());
        yield put(push("/login"))
      }
    } else if (response?.data) {
      if (response.status === 500) {
        yield put(AuthActions.signUpFailure("Server response error"))
      } else {
        yield put(AuthActions.signUpFailure(response.data))
      }
    } else {
      yield put(AuthActions.signUpFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.signUpFailure(`Unknown error: ${e}`))
  }
}

export function* shopifyAuth(api, { shop }) {
  try {
    const response = yield call(api.shopifyAuth, shop)
    if (response?.ok) {
      yield put(AuthActions.socialAuthReset())
      if (response?.data?.url) window.location.href = response.data.url
    } else {
      yield put(AuthActions.socialAuthFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.socialAuthFailure(`Unknown error: ${e}`))
  }
}
export function* socialAuth(api, { accessToken, socialType, user, isReferralOutsider }) {
  try {
    const { successfulRedirectPath } = accessToken ?? {}
    const response = yield call(api.socialAuth, accessToken, socialType, user, isReferralOutsider)
    const historyLocal = yield select((state) => state.router)
    const nextCreateAccountStep = yield select(UserSelectors.getNextCreateAccountStep)
    let userInfo = response?.userInfo
    if (response?.access && userInfo) {
      yield put(AuthActions.socialAuthSuccess(userInfo, response.access, response.refresh))

      if (socialType === "shopify") {
        window.dataLayer = window?.dataLayer || []
        window.dataLayer.push({
          event: "shopify_app_install",
          email: userInfo?.email,
          user_id: userInfo?.id,
          ...(accessToken?.shop ? { shop_url: accessToken?.shop } : {})
        })
      }

      yield put(AuthActions.socialAuthReset())
      if (userInfo?.["is_premium_available"])
        yield put(AuthActions.setShowModalPremiumFeatures(true))
      if (response?.data?.url) window?.location?.replace(response?.data?.url)

      yield delay(500)
      if (isOnboardingFinished(userInfo) && userInfo?.["email_added"]) {
        if (historyLocal?.location?.state?.reference) {
          yield put(replace(historyLocal?.location?.state?.reference))
        } else {
          if (nextCreateAccountStep) {
            yield put(push(createAccountStepPath[nextCreateAccountStep]))
          } else if (successfulRedirectPath) {
            yield put(push(successfulRedirectPath))
          } else {
            yield put(push(defaultInnerPage))
          }
        }
      } else if (userInfo?.["email_added"]) {
        if (!userInfo?.["is_email_verified"]) {
          yield put(push("/getting_started"))
        } else {
          if (userInfo?.["next_step"]) {
            yield delay(500)
            yield put(replace(onboardingStepPath[userInfo?.["next_step"]]))
          }
        }
      } else {
        yield put(push("/add-email"))
      }
    } else if (response?.data) {
      // if (response?.data?.url) window?.location?.replace(response?.data?.url);
      if (response?.data?.url) {
        const url = response.data.url
        if (url.includes("next_page")) {
          localStorage.setItem("OAuthRedirect", getOAuthRedirectUrl(socialType, accessToken))
          window?.location?.replace("/sign-up")
        } else {
          window?.location?.replace(url)
        }
      }
      if (response.status === 401) {
        const errorPayload = response?.data?.message || response?.data

        if (errorPayload?.code === "mfa_required" && errorPayload?.link) {
          const is2FAConfigured =
            typeof errorPayload?.is_2fa_configured === "string"
              ? errorPayload.is_2fa_configured.toLowerCase() === "true"
              : Boolean(errorPayload?.is_2fa_configured)

          yield put(AuthActions.authSetProp("ephemeralToken", errorPayload.link))
          yield put(AuthActions.authSetProp("is2FAConfigured", is2FAConfigured))
          yield put(AuthActions.authErrorReset())
          yield put(
            push(
              is2FAConfigured
                ? getAdminTwoFactorVerificationRoute(errorPayload.link)
                : getAdminTwoFactorSetupRoute(errorPayload.link)
            )
          )
          return
        }

        if (
            response?.data?.code === 'password_expired' &&
            response?.data?.isAdmin
          ) {
            yield put(AuthActions.socialAuthFailure(response.data));
            yield delay(500);
            yield put(AuthActions.authSetProp('tempEmail', response?.data?.email));
            yield put(AuthActions.authErrorReset());
            yield delay(500);
            yield put(push('/create-new-password'));
            return;
          }

          yield put(AuthActions.socialAuthFailure(null))
      } else if (response.status === 500) {
        yield put(AuthActions.socialAuthFailure("Server response error"))
      } else {
        yield put(AuthActions.socialAuthFailure(response.data))
      }
    } else {
      yield put(AuthActions.socialAuthFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.socialAuthFailure(`Unknown error: ${e}`))
  }
}
export function* logout(api) {
  try {
    yield call(api.logout);
  } catch (e) {
    console.log("error:", e);
  } finally {
    // Always clear the session and redirect, regardless of the logout
    // request result (an expired token makes /auth/logout/ return 401 too).
    api.updateAccessToken(null, null);
    localStorage.removeItem("isRedirectedToCompleteBa");
    yield put(AuthActions.socialAuthReset());
    yield put(push("/login"));

    // Intercom is third-party and may be blocked/unbooted — never let it
    // throw and skip the redirect above.
    try {
      shutdown();
    } catch (e) {
      console.log("intercom shutdown error:", e);
    }
  }
}

export function* socialAuthConnect(api, { accessToken, socialType, user }) {
  try {
    const response = yield call(api.socialAuthAdd, accessToken, socialType, user)
    if (response?.access && response?.userInfo) {
      yield put(AuthActions.socialAuthSuccess(response.userInfo, response.access, response.refresh))
      yield delay(500)
    } else if (response?.data) {
      if (response?.status === 401) {
        yield put(AuthActions.socialAuthFailure(null))
      } else if (response?.status === 500) {
        yield put(AuthActions.socialAuthFailure("Server response error"))
      } else {
        yield put(AuthActions.socialAuthFailure(response.data))
      }
    } else {
      yield put(AuthActions.socialAuthFailure("Server response error"))
    }
  } catch (e) {
    yield put(AuthActions.socialAuthFailure(`Unknown error: ${e}`))
  }
}

export function* socialAuthDelete(api, { socialType }) {
  try {
    const response = yield call(api.socialAuthDelete, socialType)
    if (response?.ok) {
      yield put(AuthActions.socialAuthDeleteSuccess(response.data))
    } else {
      if (response?.data) {
        if (response?.status === 401) {
          const res = yield call(api.tokenRefresh)
          if (res?.access) {
            yield put(AuthActions.updateTokens(res.access, res.refresh))
            yield* socialAuthDelete(api, { socialType })
          } else {
            yield put(AuthActions.logout())
            yield put(push("/login"))
          }
        } else {
          yield put(ErrorsActions.errorSave(response.data))
        }
      } else {
        yield put(ErrorsActions.errorSave("Server response error"))
      }
      yield put(AuthActions.authFetchingReset())
    }
  } catch (e) {
    yield put(AuthActions.authFetchingReset())
    yield put(ErrorsActions.errorSave(`Unknown error: ${e}`))
  }
}

const actions = {
  refreshTokenActions: {
    request: AuthActions.refreshTokenRequest,
    success: AuthActions.refreshTokenSuccess,
    errors: AuthActions.refreshTokenFailure
  },
  verifyTokenActions: {
    request: AuthActions.verifyTokenRequest,
    success: AuthActions.verifyTokenSuccess,
    errors: AuthActions.verifyTokenFailure
  },
  changeAdminPasswordActions: {
    request: AuthActions.changeAdminPasswordRequest,
    success: AuthActions.changeAdminPasswordSuccess,
    errors: AuthActions.changeAdminPasswordFailure
  }
}

const eventsOptions = {
  [AuthTypes.REFRESH_TOKEN_REQUEST]: {
    api: services.refreshToken,
    actions: actions.refreshTokenActions
  },
  [AuthTypes.VERIFY_TOKEN_REQUEST]: {
    api: services.verifyToken,
    actions: actions.verifyTokenActions
  },
  [AuthTypes.CHANGE_ADMIN_PASSWORD_REQUEST]: {
    api: services.changeAdminPassword,
    actions: actions.changeAdminPasswordActions
  }
}

// REFRESH TOKEN
function* refreshTokenGenerator(action) {
  const provider = eventsOptions[action.type];
  const params = action.payload;

  try {
    const response = yield call(provider.api, params);

    if (response.ok) {
      yield put(provider.actions.success(response?.data));
      // reload page after first refresh token
      window.location.reload();
      return;
    }

    yield put(
      provider.actions.errors({ errors: response?.data?.message?.detail })
    );
    // refresh failed (e.g. refresh token expired) -> logout + redirect
    yield put(AuthActions.logout());
  } catch (e) {
    // never let an unexpected error kill the watcher — always logout so the
    // user is redirected to login instead of staying with an expired token
    yield put(provider.actions.errors({ errors: e?.message || 'Refresh failed' }));
    yield put(AuthActions.logout());
  }
}

// VERIFY TOKEN
function* verifyTokenGenerator(action) {
  const provider = eventsOptions[action.type];

  try {
    const { token } = action.payload
    const response = yield call(provider.api, { token })

    if (response.status === 200) {
      yield put(provider.actions.success(response.data))
    }

    if (response.status === 401 || response.status === 403) {
      yield put(provider.actions.errors({ errors: "error 401 || 403" }))

      throw response
    }

    if (response.status === 404) {
      throw response
    }
  } catch (errors) {
    yield put(provider.actions.errors({ errors }))

    // refresh token
    yield put(actions.refreshTokenActions.request(action.payload))
  }
}

export function* changeAdminPasswordGenerator(action) {
  const provider = eventsOptions[action.type]
  const params = action.payload
  try {
    const response = yield call(provider.api, params)
    if (response && response.ok && response?.data) {
      const successMessage = response?.data?.message?.detail || "Password set successfully!"
      yield put(provider.actions.success(successMessage))
      yield put(AuthActions.authSetProp("tempEmail", null)) //clear tempEmail
      yield delay(500)
      yield put(push("/login"))
      return
    } else if (response?.data) {
      if (response.status === 500) {
        yield put(provider.actions.errors("Server response error"))
      } else {
        yield put(provider.actions.errors(response.data))
      }
    } else {
      yield put(provider.actions.errors("Server response error"))
    }
  } catch (errors) {
    yield put(provider.actions.errors({ errors }))
  }
}

export function* apiAuthSaga() {
  yield takeLatest(AuthTypes.REFRESH_TOKEN_REQUEST, refreshTokenGenerator)
  yield takeLatest(AuthTypes.VERIFY_TOKEN_REQUEST, verifyTokenGenerator)
  yield takeLatest(AuthTypes.CHANGE_ADMIN_PASSWORD_REQUEST, changeAdminPasswordGenerator)
}
